import { Stack, Group, Text, Card } from '@repletoryreach/sdk-ui';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { normaliseName, type TemplateDraft } from './draft';

/**
 * WhatsApp template languages, as Meta codes.
 *
 * A free text field here created permanently wrong templates that could only
 * be deleted and re-made, because the language is part of the key Meta files a
 * template under and cannot be changed afterwards. Only languages on Meta's
 * "Supported Languages" list for templates (read 1 November 2026): isiXhosa and
 * Sesotho were offered here but Meta has no template language for them, so
 * those templates could never be approved. The chatbot still chats in them.
 */
export const LANGUAGES = [
  { value: 'en_US', label: 'English (US)' },        // i18n-ignore: language names are written in their own language, never translated
  { value: 'en_GB', label: 'English (UK)' },        // i18n-ignore: as above
  { value: 'en_ZA', label: 'English (South Africa)' },  // i18n-ignore: as above
  { value: 'af', label: 'Afrikaans' },
  { value: 'zu', label: 'isiZulu' },
  { value: 'sw', label: 'Kiswahili' },              // i18n-ignore: as above
  { value: 'ha', label: 'Hausa' },                  // i18n-ignore: as above
  { value: 'rw_RW', label: 'Ikinyarwanda' },        // i18n-ignore: as above
  { value: 'pt_PT', label: 'Português' },           // i18n-ignore: as above
  { value: 'pt_BR', label: 'Português (Brasil)' },  // i18n-ignore: as above
  { value: 'fr', label: 'Français' },               // i18n-ignore: as above
  { value: 'ar', label: 'العربية' }                 // i18n-ignore: as above
];

const CATEGORIES = ['utility', 'marketing', 'authentication'] as const;

/**
 * Step 2: what the template is called, in what language, and which category.
 *
 * The category is the field with money attached: Meta prices a marketing
 * conversation differently from a utility one and reviews it more strictly, and
 * nothing on the old drawer said so. Each option therefore carries one plain
 * commercial sentence rather than only its name.
 */
export function BasicsStep({
  draft,
  onChange,
  nameError,
  lockIdentity
}: {
  draft: TemplateDraft;
  onChange: (next: TemplateDraft) => void;
  nameError?: string;
  /** True when editing: Meta files a template under name + language for life. */
  lockIdentity: boolean;
}) {
  const t = useT();
  const normalised = normaliseName(draft.name);
  const changed = normalised !== draft.name.trim();

  return (
    <Stack gap="lg">
      <TextInput
        label={t('templates.wizard.name')}
        /*
         * The hint goes UNDER the control, and it says what the name will
         * become rather than only what the rule is: Meta accepts lower case,
         * digits and underscores, so "Order confirmation" silently becomes
         * "order_confirmation" and a person should see that before they save,
         * not afterwards in a list.
         */
        description={
          draft.name.trim().length === 0
            ? t('templates.wizard.name.hint')
            : changed
              ? t('templates.wizard.name.willBecome', { name: normalised })
              : t('templates.wizard.name.ok')
        }
        error={nameError}
        value={draft.name}
        onChange={(e) => onChange({ ...draft, name: e.currentTarget.value })}
        disabled={lockIdentity}
        required
      />

      <Select
        label={t('common.language')}
        description={lockIdentity ? t('templates.wizard.language.locked') : t('templates.wizard.language.hint')}
        value={draft.language}
        onChange={(e) => onChange({ ...draft, language: e.currentTarget.value })}
        data={LANGUAGES}
        disabled={lockIdentity}
      />

      <Stack gap={8}>
        <Text role="inputLabel">{t('templates.columns.category')}</Text>
        <div className="rr-tplwiz__cats" role="radiogroup" aria-label={t('templates.columns.category')}>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={draft.category === c}
              className={`rr-tplwiz__cat${draft.category === c ? ' is-active' : ''}`}
              onClick={() => onChange({ ...draft, category: c })}
            >
              <span className="rr-tplwiz__cat-name">{t(`templates.category.${c}`)}</span>
              <span className="rr-tplwiz__cat-what">{t(`templates.wizard.category.${c}.what`)}</span>
              <span className="rr-tplwiz__cat-cost">{t(`templates.wizard.category.${c}.cost`)}</span>
            </button>
          ))}
        </div>
        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('templates.wizard.category.hint')}
        </Text>
      </Stack>

      {draft.category === 'authentication' && (
        <Card withBorder className="rr-tplwiz__note">
          <Group gap={10} align="flex-start" wrap="nowrap">
            <Text role="bodySmall">{t('templates.wizard.category.authentication.warning')}</Text>
          </Group>
        </Card>
      )}
    </Stack>
  );
}
